import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { inseeTownshipToString } from "@/models/identity/FrenchIdentity.ts";
import { Patient } from "@/models/PatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { DateUtils } from "@/utils/dateUtils.ts";

type PatientProps = {
  patientInformation: Patient;
};

type PatientProfileProps = {
  patientId: string;
};

export const PatientProfile: React.FC<PatientProfileProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const patient = Queries.practitioner.usePatient(patientId);

  return (
    <Card>
      <CardHeader title={t("pages.account.generalInformation.title")} />
      <CardContent className={styles.body}>
        <div className={styles.content}>
          <div className={cx(styles.multiCol)}>
            <div className={styles.column}>
              <Typography variant="body">{`${t("profile.email")} : ${
                patient.email ?? t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.phone")} : ${
                patient.contactPhone ?? t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">
                {t("profile.address")} :{" "}
                {patient.addressLine && patient.addressPostalCode && patient.addressCity
                  ? `${patient.addressLine}, ${patient.addressPostalCode} ${patient.addressCity}`
                  : t("profile.notGiven")}
              </Typography>
              <Typography variant="body">{`${t("profile.usedFirstname")} : ${
                patient.givenName ?? t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.usedLastname")} : ${
                patient.familyName ?? t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.identityFirstname")} : ${
                patient.identityFirstnames.length > 0 ? patient.identityFirstnames[0] : t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.identityFirstnames")} : ${
                patient.identityFirstnames.length > 0 ? patient.identityFirstnames.join(", ") : t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.identityLastname")} : ${
                patient.identityLastname ?? t("profile.notGiven")
              }`}</Typography>
            </div>
            <div className={styles.column}>
              <Typography variant="body">{`${t("profile.gender")} : ${
                patient.gender == "male"
                  ? t("profile.male")
                  : patient.gender == "female"
                    ? t("profile.female")
                    : t("profile.notGiven")
              }`}</Typography>
              <BirthDateDetailsInfo patientInformation={patient} />
              <BirthLocationDetailsInfo patientInformation={patient} />
              <Typography variant="body">{`${t("profile.ins")} : ${
                patient.ins ? patient.ins : t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.oid")} : ${
                patient.oid ? patient.oid : t("profile.notGiven")
              }`}</Typography>
              <Typography variant="body">{`${t("profile.ssn")} : ${
                patient.socialSecurityNumber ?? t("profile.notGiven")
              }`}</Typography>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BirthDateDetailsInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const { t } = useTranslation();
  if (!patientInformation.birthDate) {
    return <Typography variant="body">{`${t("profile.birthDate")} : ${t("profile.notGiven")}`}</Typography>;
  }
  const age = DateUtils.age(patientInformation.birthDate);
  const birthDate = patientInformation.birthDate.toLocaleString();
  const ageText = `(${t("pages.patientMonitoring.patient.age", {
    age,
  })})`;

  return <Typography variant="body">{`${t("profile.birthDate")} : ${birthDate} ${ageText}`}</Typography>;
};

const BirthLocationDetailsInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const { t } = useTranslation();
  if (!patientInformation.birthCountry) {
    return <Typography variant="body">{`${t("profile.birthPlace")} : ${t("profile.notGiven")}`}</Typography>;
  }
  const birthLocation = patientInformation.birthLocation
    ? `${t("patientInfo.placeOfBirth", {
        context: patientInformation.gender,
      })} ${inseeTownshipToString(patientInformation.birthLocation)}, ${patientInformation.birthCountry}`
    : `${t("patientInfo.countryOfBirth", {
        context: patientInformation.gender,
      })} ${patientInformation.birthCountry}`;

  return <Typography variant="body">{`${t("profile.birthPlace")} : ${birthLocation}`}</Typography>;
};

const makeStyles = (theme: Theme) => ({
  body: css``,
  content: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  multiCol: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(30)};
  `,
  column: css`
    display: flex;
    flex: 1 1 50%;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    > p {
      white-space: pre-wrap;
      word-break: break-word;
    }
  `,
  itemRow: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  icon: css`
    width: ${FontSizes.body};
    height: ${FontSizes.body};
    margin: 0 ${theme.spacing(4)};
  `,
});
